"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import en from "react-phone-number-input/locale/en";
import { Select } from "@/components/ui/select";
import type { InputVariantProps } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import timezoneCountries from "@/lib/geo/timezone-countries.json";

interface TimezoneOption {
  value: string;
  label: string;
  countryLabel: string;
  searchText: string;
  offset: string;
  offsetMinutes: number;
}

const PRIORITY_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Kolkata",
  "Australia/Sydney",
];

export function getUtcOffset(tz: string): { label: string; minutes: number } {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(now);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    const offsetStr = offsetPart?.value ?? "UTC";

    const match = offsetStr.match(/GMT([+-]\d{1,2}(?::?\d{2})?)/);
    if (!match) return { label: "UTC+0", minutes: 0 };

    const raw = match[1];
    const sign = raw.startsWith("-") ? -1 : 1;
    const cleaned = raw.replace(/^[+-]/, "");
    const [h, m] = cleaned.includes(":")
      ? cleaned.split(":").map(Number)
      : [Number(cleaned), 0];

    const totalMinutes = sign * (h * 60 + m);
    const hStr = String(Math.abs(Math.floor(totalMinutes / 60)));
    const mStr = String(Math.abs(totalMinutes % 60)).padStart(2, "0");
    const signStr = totalMinutes >= 0 ? "+" : "-";

    return {
      label: `UTC${signStr}${hStr}${mStr !== "00" ? `:${mStr}` : ""}`,
      minutes: totalMinutes,
    };
  } catch {
    return { label: "UTC+0", minutes: 0 };
  }
}

export function formatTimezoneCityLabel(timezone: string): string {
  if (timezone === "UTC") return "UTC";

  const city = timezone.split("/").pop()?.replace(/_/g, " ") ?? timezone;
  return titleCasePreserveAcronyms(city);
}

export function formatTimezoneLabel(timezone: string): string {
  if (timezone === "UTC") return "UTC";

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "longGeneric",
    });
    const name = formatter
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    if (name && !/^GMT/i.test(name)) return name;
  } catch {
    // Fall back to the IANA city segment below.
  }

  return formatTimezoneCityLabel(timezone);
}

export function formatTimezoneCountry(timezone: string): string {
  const codes = (timezoneCountries as Record<string, string>)[timezone];
  if (!codes) return "";
  const code = codes.split(",")[0];
  return (en as Record<string, string>)[code] ?? code;
}

const ALL_TIMEZONES: TimezoneOption[] = (() => {
  let zones: string[];
  try {
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    zones = [
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "America/Anchorage",
      "Pacific/Honolulu",
      "America/Toronto",
      "America/Vancouver",
      "America/Sao_Paulo",
      "America/Mexico_City",
      "America/Argentina/Buenos_Aires",
      "Europe/London",
      "Europe/Paris",
      "Europe/Berlin",
      "Europe/Madrid",
      "Europe/Rome",
      "Europe/Amsterdam",
      "Europe/Zurich",
      "Europe/Moscow",
      "Europe/Istanbul",
      "Europe/Warsaw",
      "Europe/Stockholm",
      "Asia/Tokyo",
      "Asia/Shanghai",
      "Asia/Kolkata",
      "Asia/Dubai",
      "Asia/Singapore",
      "Asia/Hong_Kong",
      "Asia/Seoul",
      "Asia/Bangkok",
      "Asia/Jakarta",
      "Asia/Karachi",
      "Asia/Dhaka",
      "Asia/Taipei",
      "Asia/Jerusalem",
      "Asia/Riyadh",
      "Australia/Sydney",
      "Australia/Melbourne",
      "Australia/Perth",
      "Pacific/Auckland",
      "Pacific/Fiji",
      "Africa/Cairo",
      "Africa/Lagos",
      "Africa/Johannesburg",
      "Africa/Nairobi",
      "UTC",
    ];
  }

  const mapped = zones.map((tz) => {
    const { label, minutes } = getUtcOffset(tz);
    const genericLabel = formatTimezoneLabel(tz);
    const cityLabel = formatTimezoneCityLabel(tz);
    const countryLabel = formatTimezoneCountry(tz);
    return {
      value: tz,
      label: genericLabel,
      countryLabel,
      searchText:
        `${genericLabel} ${cityLabel} ${countryLabel} ${tz}`.toLowerCase(),
      offset: label,
      offsetMinutes: minutes,
    };
  });

  // Many IANA zones resolve to the same friendly name + offset (e.g. every
  // Argentina city → "Argentina Standard Time, UTC-3"). Collapse those into a
  // single row so the list mirrors the selected label without duplicates.
  const seen = new Set<string>();
  const result: TimezoneOption[] = [];
  const pushUnique = (m: TimezoneOption) => {
    const key = `${m.label}|${m.offsetMinutes}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(m);
  };

  for (const tz of PRIORITY_TIMEZONES) {
    const m = mapped.find((x) => x.value === tz);
    if (m) pushUnique(m);
  }

  mapped
    .filter((m) => !PRIORITY_TIMEZONES.includes(m.value))
    .sort(
      (a, b) =>
        a.offsetMinutes - b.offsetMinutes || a.label.localeCompare(b.label),
    )
    .forEach(pushUnique);

  return result;
})();

interface TimezoneSelectProps {
  value: string;
  onChange: (timezone: string) => void;
  variant?: InputVariantProps["variant"];
  size?: InputVariantProps["size"];
  className?: string;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function TimezoneSelect({
  value,
  onChange,
  variant = "default",
  size,
  className,
  disabled,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: TimezoneSelectProps) {
  useEffect(() => {
    if (variant === "default" && !value) {
      onChange(detectBrowserTimezone());
    }
  }, [value, onChange, variant]);

  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? openProp : uncontrolledOpen;
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  const handleOpenChange = useCallback(
    (val: boolean) => {
      if (!val) setSearch("");
      onOpenChangeProp?.(val);
      if (!isControlled) setUncontrolledOpen(val);
    },
    [isControlled, onOpenChangeProp],
  );

  const selected = useMemo(
    () =>
      ALL_TIMEZONES.find((tz) => tz.value === value) || {
        value,
        label: formatTimezoneLabel(value),
        countryLabel: formatTimezoneCountry(value),
        searchText:
          `${formatTimezoneLabel(value)} ${formatTimezoneCityLabel(value)} ${formatTimezoneCountry(value)} ${value}`.toLowerCase(),
        offset: getUtcOffset(value).label,
        offsetMinutes: getUtcOffset(value).minutes,
      },
    [value],
  );

  const filtered = useMemo(() => {
    if (!search) return ALL_TIMEZONES;
    const q = search.toLowerCase();
    return ALL_TIMEZONES.filter(
      (tz) =>
        tz.searchText.includes(q) ||
        tz.offset.toLowerCase().includes(q),
    );
  }, [search]);

  const handleSelect = useCallback(
    (tz: TimezoneOption) => {
      onChange(tz.value);
      handleOpenChange(false);
    },
    [onChange, handleOpenChange],
  );

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => {
      searchRef.current?.focus();
      activeRef.current?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  return (
    <Select.Root open={open} onOpenChange={handleOpenChange}>
      <Select.Trigger
        variant={variant}
        size={size}
        className={cn("w-full justify-between", className)}
        disabled={disabled}
        active={!!value}
      >
        {value ? (
          <Select.TriggerContent className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-heading">{selected.label}</span>
            <span className="shrink-0 text-contrast/50">({selected.offset})</span>
          </Select.TriggerContent>
        ) : (
          <Select.TriggerContent className="text-contrast/30">
            Select timezone...
          </Select.TriggerContent>
        )}
        <Select.Icon className="ml-auto" />
      </Select.Trigger>

      <Select.Content
        onWheelCapture={(e) => {
          e.stopPropagation();
        }}
      >
        <Select.Search
          ref={searchRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search timezones..."
        />

        <Select.List
          className="max-h-[210px]"
          onWheelCapture={(e) => {
            e.stopPropagation();
          }}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-contrast/40">
              No timezones found
            </div>
          ) : (
            filtered.map((tz) => (
              <Select.Item
                key={tz.value}
                ref={tz.value === value ? activeRef : undefined}
                selected={tz.value === value}
                onClick={() => handleSelect(tz)}
                className="py-1.5"
              >
                <span className="min-w-0 flex-1 truncate text-left text-sm text-heading">
                  {tz.label}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-sm text-heading/50">
                  {tz.countryLabel ? (
                    <span className="truncate max-w-[140px]">
                      {tz.countryLabel}
                    </span>
                  ) : null}
                  <span className="shrink-0 tabular-nums">({tz.offset})</span>
                </span>
              </Select.Item>
            ))
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}

export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/New_York";
  }
}
